import { useState, useEffect, useRef, useCallback } from 'react';

import sleep from '../helpers/sleep';
import useIsMounted from './useIsMounted';
import { defaultShouldLoadMore } from '../helpers/defaultShouldLoadMore';
import { defaultReduceOptions } from '../helpers/defaultReduceOptions';

const errorText =
  '[react-select-async-paginate] response of "loadOptions" should be an object with "options" prop, which contains array of options.';

export const validateResponse = (console, response) => {
  if (!response) {
    console.error(errorText, 'Received:', response);
    throw new Error(errorText);
  }

  if (!Array.isArray(response.options)) {
    console.error(errorText, 'Received:', response);
    throw new Error(errorText);
  }
};

export const getInitialOptionsCache = ({
  options,
  defaultOptions,
  additional,
  defaultAdditional,
}) => {
  const initialOptions =
    defaultOptions === true ? null : defaultOptions instanceof Array ? defaultOptions : options;

  if (initialOptions) {
    return {
      '': {
        isFirstLoad: false,
        isLoading: false,
        options: initialOptions,
        hasMore: true,
        additional: defaultAdditional || additional,
      },
    };
  }

  return {};
};

export const getInitialCache = (params) => ({
  isFirstLoad: true,
  options: [],
  hasMore: true,
  isLoading: false,
  additional: params.additional,
});

export const requestOptions = async (
  paramsRef,
  optionsCacheRef,
  debounceTimeout,
  sleepParam,
  setOptionsCache,
  validateResponseParam,
  reduceOptions
) => {
  const currentInputValue = paramsRef.current.inputValue;

  const isCacheEmpty = !optionsCacheRef.current[currentInputValue];

  const currentOptions = isCacheEmpty
    ? getInitialCache(paramsRef.current)
    : optionsCacheRef.current[currentInputValue];

  if (currentOptions.isLoading || !currentOptions.hasMore) {
    return;
  }

  setOptionsCache((prevOptionsCache) => ({
    ...prevOptionsCache,
    [currentInputValue]: {
      ...currentOptions,
      isLoading: true,
    },
  }));

  if (debounceTimeout > 0) {
    await sleepParam(debounceTimeout);

    const newInputValue = paramsRef.current.inputValue;

    if (currentInputValue !== newInputValue) {
      setOptionsCache((prevOptionsCache) => {
        if (isCacheEmpty) {
          // eslint-disable-next-line no-unused-vars
          const { [currentInputValue]: itemForDelete, ...restCache } = prevOptionsCache;

          return restCache;
        }

        return {
          ...prevOptionsCache,
          [currentInputValue]: {
            ...currentOptions,
            isLoading: false,
          },
        };
      });

      return;
    }
  }

  let response;
  let hasError;

  try {
    const { loadOptions } = paramsRef.current;

    response = await loadOptions(
      currentInputValue,
      currentOptions.options,
      currentOptions.additional
    );

    hasError = false;
  } catch (e) {
    hasError = true;
  }

  if (hasError) {
    setOptionsCache((prevOptionsCache) => ({
      ...prevOptionsCache,
      [currentInputValue]: {
        ...currentOptions,
        isLoading: false,
      },
    }));

    return;
  }

  validateResponseParam(console, response);

  const { options, hasMore } = response;

  // eslint-disable-next-line no-prototype-builtins
  const newAdditional = response.hasOwnProperty('additional')
    ? response.additional
    : currentOptions.additional;

  setOptionsCache((prevOptionsCache) => ({
    ...prevOptionsCache,
    [currentInputValue]: {
      ...currentOptions,
      options: reduceOptions(currentOptions.options, options, newAdditional),
      hasMore: !!hasMore,
      isLoading: false,
      isFirstLoad: false,
      additional: newAdditional,
    },
  }));
};

export const increaseStateId = (prevStateId) => prevStateId + 1;

export const useAsyncPaginateBasePure = (
  useRefParam,
  useStateParam,
  useEffectParam,
  useCallbackParam,
  useIsMountedParam,
  validateResponseParam,
  getInitialOptionsCacheParam,
  requestOptionsParam,
  params,
  deps = []
) => {
  const {
    defaultOptions,
    loadOptionsOnMenuOpen = true,
    debounceTimeout = 0,
    inputValue,
    menuIsOpen,
    filterOption = null,
    reduceOptions = defaultReduceOptions,
    shouldLoadMore = defaultShouldLoadMore,
  } = params;

  const isMounted = useIsMountedParam();

  const isInitRef = useRefParam(true);
  const paramsRef = useRefParam(params);

  paramsRef.current = params;

  const setStateId = useStateParam(0)[1];

  const optionsCacheRef = useRefParam(null);

  if (optionsCacheRef.current === null) {
    optionsCacheRef.current = getInitialOptionsCacheParam(params);
  }

  const callRequestOptions = useCallbackParam(() => {
    requestOptionsParam(
      paramsRef,
      optionsCacheRef,
      debounceTimeout,
      sleep,
      (reduceState) => {
        optionsCacheRef.current = reduceState(optionsCacheRef.current);

        if (isMounted()) {
          setStateId(increaseStateId);
        }
      },
      validateResponseParam,
      reduceOptions
    );
  }, []);

  const handleScrolledToBottom = useCallbackParam(() => {
    const currentInputValue = paramsRef.current.inputValue;
    const currentOptions = optionsCacheRef.current[currentInputValue];

    if (currentOptions) {
      callRequestOptions();
    }
  }, []);

  useEffectParam(() => {
    if (isInitRef.current) {
      isInitRef.current = false;
    } else {
      optionsCacheRef.current = {};
      setStateId(increaseStateId);
    }

    if (defaultOptions === true) {
      callRequestOptions();
    }
  }, deps);

  useEffectParam(() => {
    if (menuIsOpen && !optionsCacheRef.current[inputValue]) {
      callRequestOptions();
    }
  }, [inputValue]);

  useEffectParam(() => {
    if (menuIsOpen && !optionsCacheRef.current[''] && loadOptionsOnMenuOpen) {
      callRequestOptions();
    }
  }, [menuIsOpen]);

  const currentOptions = optionsCacheRef.current[inputValue] || getInitialCache(params);

  return {
    handleScrolledToBottom,
    shouldLoadMore,
    filterOption,
    isLoading: currentOptions.isLoading,
    isFirstLoad: currentOptions.isFirstLoad,
    options: currentOptions.options,
  };
};

export const useAsyncPaginateBase = (params, deps = []) =>
  useAsyncPaginateBasePure(
    useRef,
    useState,
    useEffect,
    useCallback,
    useIsMounted,
    validateResponse,
    getInitialOptionsCache,
    requestOptions,
    params,
    deps
  );
